import { useEffect, useState, useCallback } from "react";
import { api, speak } from "@/lib/api";
import { Volume2, RefreshCw, Check, X, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import PageHeader from "@/components/app/PageHeader";

const SYSTEMS = [
  { id: "hiragana", label: "Hiragana" },
  { id: "katakana", label: "Katakana" },
  { id: "kanji-n5", label: "Kanji N5" },
  { id: "kanji-n4", label: "Kanji N4" },
  { id: "kanji-n3", label: "Kanji N3" },
];
const CHOICE_TINTS = ["bg-sakura/40", "bg-ramune/40", "bg-yuzu/50", "bg-lavender/50"];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildChoices(d, i) {
  const correct = d[i];
  const pool = shuffle(d.filter((x) => x.char !== correct.char)).slice(0, 3);
  return shuffle([correct, ...pool]);
}

export default function FlashcardsPage() {
  const { user } = useAuth();
  const { t } = useLang();
  const [system, setSystem] = useState("hiragana");
  const [deck, setDeck] = useState([]);
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState("flash");
  const [flipped, setFlipped] = useState(false);
  const [choices, setChoices] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState({ right: 0, total: 0 });
  const isKanji = system.startsWith("kanji");

  const loadDeck = useCallback(async () => {
    const url = isKanji ? `/characters/kanji?level=${system.split("-")[1].toUpperCase()}` : `/characters/${system}`;
    const { data } = await api.get(url);
    setDeck(shuffle(data));
    setIndex(0);
    setScore({ right: 0, total: 0 });
  }, [system, isKanji]);

  useEffect(() => { loadDeck(); }, [loadDeck]);

  useEffect(() => {
    if (mode === "quiz" && deck.length) setChoices(buildChoices(deck, index));
    setFlipped(false);
    setFeedback(null);
  }, [index, deck, mode]);

  const current = deck[index];
  const answerLabel = (c) => (isKanji ? c.meaning : c.romaji);
  const next = () => setIndex((i) => (i + 1) % Math.max(1, deck.length));
  const saveProgress = () => {
    if (user && user !== false) api.post("/progress", { character: current.char, system: isKanji ? "kanji" : system, status: "mastered" }).catch(() => {});
  };

  const handleQuizAnswer = (choice) => {
    if (feedback) return;
    const correct = choice.char === current.char;
    setFeedback(correct ? "correct" : "wrong");
    setScore((s) => ({ right: s.right + (correct ? 1 : 0), total: s.total + 1 }));
    if (correct) saveProgress();
    setTimeout(next, 900);
  };

  if (!deck.length) return <div className="max-w-7xl mx-auto px-6 py-10 text-ink-soft">{t("loading")}</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <PageHeader eyebrow={t("practiceMode")} title={t("flashcards")}
        right={<div className="kawaii-chip !text-sm !px-4 !py-2" data-testid="score-chip"><Sparkles className="w-4 h-4 text-sakura-deep" strokeWidth={2.5} /> {score.right} / {score.total}</div>} />

      <div className="flex flex-wrap gap-3 mb-8">
        <div className="kawaii-card inline-flex flex-wrap gap-1 p-1.5">
          {SYSTEMS.map((s) => (
            <button key={s.id} data-testid={`deck-${s.id}`} onClick={() => setSystem(s.id)} className={`kawaii-pill-nav !text-xs ${system === s.id ? "active" : ""}`}>{s.label}</button>
          ))}
        </div>
        <div className="kawaii-card inline-flex gap-1 p-1.5">
          <button data-testid="mode-flash" onClick={() => setMode("flash")} className={`kawaii-pill-nav !text-xs ${mode === "flash" ? "active" : ""}`}>{t("flashcards")}</button>
          <button data-testid="mode-quiz" onClick={() => setMode("quiz")} className={`kawaii-pill-nav !text-xs ${mode === "quiz" ? "active" : ""}`}>{t("quiz")}</button>
        </div>
      </div>

      <div className={`flip-card ${flipped && mode === "flash" ? "is-flipped" : ""}`}>
        <div className="flip-inner">
          <div className={`flip-face kawaii-card !rounded-4xl aspect-[4/3] flex flex-col items-center justify-center transition-colors duration-300 ${feedback === "correct" ? "!bg-matcha/70" : feedback === "wrong" ? "!bg-coral/50 shake" : ""}`}>
            <div className="font-jp text-[7rem] sm:text-[10rem] leading-none kawaii-pop text-ink" key={current.char} data-testid="flashcard-char">{current.char}</div>
            {mode === "flash" && (
              <button data-testid="flip-btn" onClick={() => setFlipped(true)} className="kawaii-btn kawaii-btn-primary mt-6">{t("showAnswer")}</button>
            )}
          </div>
          {mode === "flash" && (
            <div className="flip-face flip-back kawaii-card !rounded-4xl aspect-[4/3] !bg-yuzu/60 flex flex-col items-center justify-center gap-3">
              <div className="font-display text-4xl sm:text-6xl text-ink" data-testid="flashcard-answer">{answerLabel(current)}</div>
              {isKanji && <div className="text-sm text-ink-soft">On: {current.onyomi} · Kun: {current.kunyomi}</div>}
              <button onClick={() => speak(current.char)} className="kawaii-btn kawaii-btn-sm"><Volume2 className="w-4 h-4" strokeWidth={2.5} /> {t("hearIt")}</button>
            </div>
          )}
        </div>
      </div>

      {mode === "flash" ? (
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <button data-testid="need-practice-btn" onClick={next} className="kawaii-btn"><RefreshCw className="w-4 h-4" strokeWidth={2.5} /> {t("needPractice")}</button>
          <button data-testid="got-it-btn" onClick={() => { saveProgress(); next(); }} className="kawaii-btn kawaii-btn-ink"><Check className="w-4 h-4" strokeWidth={2.5} /> {t("gotIt")}</button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-3">
          {choices.map((c, i) => (
            <button key={c.char} data-testid={`quiz-choice-${i}`} onClick={() => handleQuizAnswer(c)}
              className={`kawaii-btn !py-4 text-base ${feedback && c.char === current.char ? "!bg-matcha" : feedback ? "opacity-50" : CHOICE_TINTS[i]}`}>
              {answerLabel(c)}
            </button>
          ))}
        </div>
      )}

      {feedback && (
        <div className={`mt-5 kawaii-chip !text-sm !px-4 !py-2 ${feedback === "correct" ? "!bg-matcha" : "!bg-coral/60"}`} data-testid="feedback-chip">
          {feedback === "correct" ? <><Check className="w-4 h-4" strokeWidth={3} /> {t("correct")}</> : <><X className="w-4 h-4" strokeWidth={3} /> {t("answer")}: {answerLabel(current)}</>}
        </div>
      )}
    </div>
  );
}
